const normalizePinoPredictiveSearchTerm = (value) => {
  if (window.PinoSearchNormalizer) return window.PinoSearchNormalizer.normalize(value);
  if (
    (window.Shopify && window.Shopify.designMode) ||
    document.documentElement.classList.contains('shopify-design-mode') ||
    document.documentElement.lang.toLocaleLowerCase().split('-')[0] !== 'fi'
  ) return value;

  return value
    .replace(
      /(^|[^\p{L}])tulp{1,2}aani(?:[\s-]+)?tuol[\p{L}]*(?=$|[^\p{L}])/giu,
      '$1tulip chair'
    )
    .replace(
      /(^|[^\p{L}])tulp{1,2}aani(?:[\s-]+)?pö[yi][\p{L}]*(?=$|[^\p{L}])/giu,
      '$1tulip table'
    )
    .replace(/(^|[^\p{L}])tulp{1,2}aan[\p{L}]*(?=$|[^\p{L}])/giu, '$1tulip');
};

const resolvePinoPredictiveSearch = (value) => {
  if (window.PinoSearchNormalizer?.resolvePredictive) {
    return window.PinoSearchNormalizer.resolvePredictive(value);
  }
  if (window.PinoSearchNormalizer?.resolveExact) {
    return window.PinoSearchNormalizer.resolveExact(value);
  }

  const normalized = normalizePinoPredictiveSearchTerm(value);
  const clean = String(normalized || '').replace(/["\\]+/g, ' ').replace(/\s+/g, ' ').trim();
  return {
    original: String(value || '').trim(),
    normalized: clean,
    query: clean ? `"${clean}"` : '',
    exact: Boolean(clean)
  };
};

const foldPinoFinnishSearchTerm = (value) => window.PinoSearchNormalizer
  ? window.PinoSearchNormalizer.fold(value)
  : String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fi-FI')
      .replace(/[-_/]+/g, ' ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

const isPinoFinnishTulipTableTerm = (value) => {
  if (document.documentElement.lang.toLocaleLowerCase().split('-')[0] !== 'fi') return false;
  const folded = foldPinoFinnishSearchTerm(value);
  if (/^(?:tul|tulp|tulpa|tulpaa|tulpaan|tulpp|tulppa|tulppaa|tulppaan)$/.test(folded)) return true;
  return new Set([
    'tulip', 'tulip table',
    'tulpaani', 'tulppaani',
    'tulpaanipoyta', 'tulppaanipoyta',
    'tulpaani poyta', 'tulppaani poyta',
    'tulpaanipoydat', 'tulppaanipoydat',
    'tulpaanipoytaa', 'tulppaanipoytaa',
    'tulpaanipoytia', 'tulppaanipoytia'
  ]).has(folded);
};

const escapePinoSearchHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const getPinoFinnishTulipFallback = async (searchTerm) => {
  if (!isPinoFinnishTulipTableTerm(searchTerm)) return null;

  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const productUrl = `${root}products/eero-saarinen-tulip`;
  const response = await fetch(`${productUrl}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return null;

  const product = await response.json();
  if (!product.available) {
    const stockResponse = await fetch(`${productUrl}?section_id=pino-stock-status`, { credentials: 'same-origin' });
    if (!stockResponse.ok) return null;
    const node = new DOMParser().parseFromString(await stockResponse.text(), 'text/html').querySelector('[data-pino-stock-status]');
    if (!node || JSON.parse(node.textContent).state !== 'in_stock') return null;
  }

  const imageUrl = product.featured_image
    ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=150`
    : '';
  const image = imageUrl
    ? `<img class="predictive-search__image" src="${escapePinoSearchHtml(imageUrl)}" alt="" width="50" height="50" loading="lazy">`
    : '';
  const title = 'Eero Saarinen Tulip-ruokapöytä Knollille, valkoinen marmorikansi';

  return `
    <div id="predictive-search-results" role="listbox">
      <span class="visually-hidden" data-predictive-search-live-region-count-value>1 tulos</span>
      <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
        <div class="predictive-search__result-group"><div>
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Tuotteet</h2>
          <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
            <li id="predictive-search-option-product-1" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${escapePinoSearchHtml(productUrl)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
                ${image}
                <div class="predictive-search__item-content"><p class="predictive-search__item-heading h5">${escapePinoSearchHtml(title)}</p></div>
              </a>
            </li>
          </ul>
        </div></div>
      </div>
      <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
        <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
          <span data-predictive-search-search-for-text>Hae sanalla “${escapePinoSearchHtml(searchTerm)}”</span>
        </button>
      </div>
    </div>`;
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const exactSearch = resolvePinoPredictiveSearch(searchTerm);
    const backendSearchTerm = exactSearch.normalized || searchTerm;
    const queryKey = `${searchTerm}--${backendSearchTerm}`.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // The catalogue index is the authoritative predictive source in both
    // storefront languages. It starts at one character, narrows on every
    // keystroke and returns only clickable product/landing suggestions.
    if (
      !(window.Shopify && window.Shopify.designMode) &&
      !document.documentElement.classList.contains('shopify-design-mode') &&
      window.PinoSearchEngine?.suggest
    ) {
      const requestedTerm = searchTerm;
      window.PinoSearchEngine.suggest(searchTerm)
        .then((resultsMarkup) => {
          if (this.getQuery() !== requestedTerm) return;
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        })
        .catch(() => this.close());
      return;
    }

    if (isPinoFinnishTulipTableTerm(searchTerm)) {
      getPinoFinnishTulipFallback(searchTerm)
        .then((resultsMarkup) => {
          if (!resultsMarkup) {
            this.close();
            return;
          }
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        })
        .catch(() => this.close());
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(backendSearchTerm)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide&resources[options][prefix]=last&resources[options][fields]=title,vendor,product_type,variants.title&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        const searchForText = resultsSection.querySelector('[data-predictive-search-search-for-text]');
        if (searchForText && backendSearchTerm !== searchTerm) {
          const displayedBackendTerm = exactSearch.normalized || backendSearchTerm.replace(/^"|"$/g, '');
          searchForText.textContent = searchForText.textContent
            .split(backendSearchTerm).join(searchTerm)
            .split(displayedBackendTerm).join(searchTerm);
        }
        const resultsMarkup = resultsSection.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

